import React, { useEffect, useState, useContext } from 'react';
import logo from '../logo.jpg';
import { useLocation, useNavigate } from 'react-router-dom';
// legacy auth path, remove later
import { AuthContext } from '../security/AuthProvider';
//import { signIn, signUp, fetchAuthSession } from '@aws-amplify/auth';
import { signIn, signUp, confirmSignUp, resendSignUpCode, fetchAuthSession, signOut, resetPassword, confirmResetPassword } from '@aws-amplify/auth';
import axios from 'axios';
import { EnvContext } from '../App';
import { useNotification } from '../contexts/NotificationContext';

//import { Auth } from 'aws-amplify';


function LoginForm({ mode }) {
    const authProvider = useContext(AuthContext);
    const envProvider = useContext(EnvContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { showSuccess, showError, showWarning, showInfo } = useNotification();
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [awaitingResetCode, setAwaitingResetCode] = useState(false);

    useEffect(() => {
        if (location.state && location.state.error) {
            setError(location.state.error);
        }
    }, [location.state]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (email && !password) {  // Legacy login path, remove later
            // Clear all tokens before legacy login
            localStorage.removeItem('cognito_id_token');
            localStorage.removeItem('impersonation_token');
            localStorage.removeItem('impersonated_user');
            authProvider.login(email, () => {
                navigate('/time-entry', { replace: true });
            }, (error) => {
                // Check if error is related to email authorization
                const errorMessage = error && 'message' in error ? error['message'] : 
                                   (typeof error === 'string' ? error : 'Invalid Credentials. Please check email and try again.');
                
                if (errorMessage && (errorMessage.includes('Email address') || errorMessage.includes('not enabled') || errorMessage.includes('not authorized'))) {
                    // showError('Email address not authorized', 'Login Failed');
                    setError('Email address not authorized');
                } else {
                    // showError(errorMessage, 'Login Failed');
                    setError(errorMessage);
                }
            });
        } else if (email && password) { // Cognito login path
            try {
                // Validate email before attempting login
                const apiBase = envProvider.v2Api;
                try {
                    await axios.post(
                        `${apiBase}/public/time-entry-app/validateEmail`,
                        { email },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'jm-client': `web-${process.env.REACT_APP_VERSION}`,
                            },
                        }
                    );
                } catch (validationErr) {
                    // If email validation fails, show error and stop login process
                    if (validationErr.response && validationErr.response.status === 401) {
                        // showError('Email address not authorized', 'Login Failed');
                        setError('Email address not authorized');
                        return;
                    }
                    // If validation error is not 401, log it but continue (might be network issue)
                    console.warn('Email validation error:', validationErr);
                }
                
                // Always sign out first to clear any existing session
                try {
                    await signOut();
                } catch (signOutErr) {
                    // Ignore signOut errors (e.g., if not signed in)
                }
                await signIn({ username: email, password });
                const session = await fetchAuthSession();
                const accessToken = session.tokens?.accessToken?.toString();
                const idToken = session.tokens?.idToken?.toString();
                const refreshToken = session.tokens?.refreshToken?.toString();

                // if (idToken) {
                //    console.log("Copy your ID token and paste it into https://jwt.io to inspect claims:\n\n", idToken);
                //}

                // Use the ID token for backend authentication so the email claim is present
                if (idToken) {
                    // Store the Cognito JWT for use in API requests
                    localStorage.setItem('cognito_id_token', idToken);
                    
                    // Verify refresh token is available (Cognito manages it internally, but good to log)
                    if (refreshToken) {
                        console.log('Refresh token available - sliding window enabled');
                    } else {
                        console.warn('No refresh token - sliding window may not work');
                    }
                    // Only call authProvider.login to handle session and API call
                    if (authProvider && typeof authProvider.login === 'function') {
                        authProvider.login(email, () => {
                            navigate('/time-entry', { replace: true });
                        }, (error) => {
                            // Handle error from initSession - check if it's an email authorization error
                            if (error && typeof error === 'string' && error.includes('Email address')) {
                                // showError('Email address not authorized', 'Login Failed');
                                setError('Email address not authorized');
                            } else if (error && typeof error === 'object' && error.message && error.message.includes('Email address')) {
                                // showError('Email address not authorized', 'Login Failed');
                                setError('Email address not authorized');
                            } else {
                                // For other errors, show generic message
                                // showError('Login failed. Please try again.', 'Login Failed');
                                setError('Login failed. Please try again.');
                            }
                        });
                    } else {
                        navigate('/time-entry', { replace: true });
                    }
                } else {
                    // showError('Could not retrieve ID token.', 'Authentication Error');
                    setError('Unable to login, please validate your email address using Sign Up');
                }
            } catch (err) {
                console.error('Login error:', err);
                const errorMessage = err.message || '';
                if (errorMessage.includes('Password attempts exceeded')) {
                    // showError('Too many failed login attempts. Please try again later or use Forgot password to reset.', 'Login Failed');
                    setError('Too many failed login attempts. Please try again later or use Forgot password to reset.');
                } else if (err.name === 'UserNotFoundException' || err.name === 'NotAuthorizedException') {
                    // showError('User does not exist or password is incorrect.', 'Login Failed');
                    setError('User does not exist or password is incorrect.');
                } else if (err.response && err.response.data && err.response.data.message) {
                    // showError(err.response.data.message, 'Login Failed');
                    setError(err.response.data.message);
                } else {
                    // showError(errorMessage || 'Unknown error', 'Login Failed');
                    setError(errorMessage || 'Unknown error');
                }
            }
        } else {
            setError('Please enter your email address.');
        }
    };


    const handleSignUp = async (e) => {
        e.preventDefault();
        if (!email || !email.trim()) {
            setError('Please enter your email address.');
            return;
        }
        if (!password || !password.trim()) {
            setError('Please enter a password.');
            return;
        }
        // Do not clear error here, only clear on successful validation/signup
        try {
            // Call backend to validate email before proceeding
            const apiBase = envProvider.v2Api;
            const validateResp = await axios.post(
                `${apiBase}/public/time-entry-app/validateEmail`,
                { email }, // send as JSON object for DTO compatibility
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'jm-client': `web-${process.env.REACT_APP_VERSION}`,
                    },
                }
            );
            // If we get here, email is valid, proceed with Cognito signUp
            const result = await signUp({
                username: email,
                password,
                options: {
                    userAttributes: { email },
                    autoSignIn: false,
                }
            });
            showSuccess('Check your email for the verification code.', 'Account Created!');
            setError(null);
            setAwaitingVerification(true);
        } catch (err) {
            console.log('SignUp error:', err);
            const errorMessage = err.message || '';
            const errorName = err.name || '';
            const errorCode = err.code || '';
            // Detect "user already exists" across browsers (Cognito may set name, code, or only message)
            const isUsernameExists = errorName === 'UsernameExistsException' ||
                errorCode === 'UsernameExistsException' ||
                errorMessage.includes('User already exists');

            // If backend returns 401 (e.g. email not registered with IMS), show inline message
            if (err.response && err.response.status === 401) {
                const msg = typeof err.response.data === 'string' ? err.response.data : (err.response.data?.message || 'Email address not registered with IMS.');
                setError(msg);
            } else if (errorMessage.includes('CONFIRMED') || errorMessage.includes('already confirmed')) {
                // Explicit \"already confirmed\" response from Cognito – direct them to sign in
                setError('This email address is already registered and confirmed. Please sign in instead.');
                setAwaitingVerification(false);
            } else if (isUsernameExists) {
                // User already exists but may not be confirmed – always show verification UI
                setAwaitingVerification(true);
                setError('Account exists but may not be verified. Enter your verification code below or use Resend Code.');
            } else {
                showError(`Failed to sign up: ${errorMessage || 'Unknown error'}`, 'Sign Up Failed');
                setError(null); // Clear inline error since we're showing toast
            }
        }
    };

    const [verificationCode, setVerificationCode] = useState('');
    const [awaitingVerification, setAwaitingVerification] = useState(false);



    const handleConfirmCode = async () => {
        try {
            await confirmSignUp({ username: email, confirmationCode: verificationCode });
            setError(null);  // Clear "Account exists but may not be verified" message
            showSuccess('Your account has been verified! You can now sign in.', 'Account Verified');
            setAwaitingVerification(false);  // Hide code input after success
            setVerificationCode('');
        } catch (err) {
            console.error('Verification error:', err);
            // Check if user is already confirmed
            if (err.message && err.message.includes('Current status is CONFIRMED')) {
                showError('This account is already verified. Please sign in instead.', 'Account Already Verified');
                setAwaitingVerification(false);
                setVerificationCode('');
            } else {
                showError(err.message || 'Unknown error', 'Verification Failed');
            }
        }
    };

    const handleResendCode = async () => {
        try {
            await resendSignUpCode({ username: email });
            showInfo('A new verification code has been sent to your email.', 'Code Sent');
        } catch (err) {
            console.error('Resend code error:', err);
            showError(err.message || 'Unknown error', 'Failed to Resend Code');
        }
    };

    const handlePasswordReset = async () => {
        if (!email) {
            setError('Please enter your email address first.');
            return;
        }
        
        try {
            await resetPassword({ username: email });
            showInfo('Password reset code sent to your email.', 'Reset Code Sent');
            setAwaitingResetCode(true);
            setError(null);
        } catch (err) {
            console.error('Password reset error:', err);
            setError(`Failed to send reset code: ${err.message || 'Unknown error'}`);
        }
    };

    const handleConfirmPasswordReset = async () => {
        if (!resetCode || !newPassword) {
            setError('Please enter both the reset code and new password.');
            return;
        }

        try {
            await confirmResetPassword({ 
                username: email, 
                confirmationCode: resetCode, 
                newPassword: newPassword 
            });
            showSuccess('You can now sign in with your new password.', 'Password Reset Successful!');
            setAwaitingResetCode(false);
            setIsResettingPassword(false);
            setResetCode('');
            setNewPassword('');
            setError(null);
        } catch (err) {
            console.error('Password reset confirmation error:', err);
            setError(`Failed to reset password: ${err.message || 'Unknown error'}`);
        }
    };


    return (
        <div className="min-h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <img className="mx-auto h-40 w-auto" src={logo} alt="Workflow" />
                    <h2 className="mt-6 text-center text-3xl tracking-tight font-bold text-gray-900">
                        Sign in to your account
                    </h2>
                </div>
                {error && (
                    <div
                        className="text-center font-semibold mt-2"
                        style={{ color: '#ef4444' }}
                    >
                        {error}
                    </div>
                )}
                <form className="mt-8 space-y-6" onSubmit={handleLogin}>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm mt-2"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <div className="flex flex-col">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <div className="text-xs text-gray-500 mt-1">
                                Must be at least 8 characters and contain: 1 number, 1 uppercase letter, 1 lowercase letter, and 1 special character
                            </div>
                        </div>
                        <div className="relative">
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm mt-2"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                            />
                        </div>
                    </div>
                    <div>
                        <button
                            type="submit"
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Sign In
                        </button>
                    </div>

                </form>
                
                {/* Forgot Password Link */}
                {!isResettingPassword && !awaitingResetCode && (
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={() => setIsResettingPassword(true)}
                            className="text-sm text-primary-600 hover:text-primary-500"
                        >
                            Forgot your password?
                        </button>
                    </div>
                )}

                {/* Password Reset Form */}
                {isResettingPassword && !awaitingResetCode && (
                    <div className="mt-4 space-y-4">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
                            <p className="text-sm text-gray-600">Enter your email to receive a reset code</p>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                type="button"
                                onClick={handlePasswordReset}
                                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                Send Reset Code
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsResettingPassword(false)}
                                className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Reset Code Confirmation Form */}
                {awaitingResetCode && (
                    <div className="mt-4 space-y-4">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900">Enter Reset Code</h3>
                            <p className="text-sm text-gray-600">Check your email for the reset code</p>
                        </div>
                        <div>
                            <label htmlFor="resetCode" className="block text-sm font-medium text-gray-700">Reset Code</label>
                            <input
                                id="resetCode"
                                name="resetCode"
                                type="text"
                                value={resetCode}
                                onChange={(e) => setResetCode(e.target.value)}
                                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm mt-2"
                                placeholder="Enter reset code"
                            />
                        </div>
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">New Password</label>
                            <input
                                id="newPassword"
                                name="newPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm mt-2"
                                placeholder="Enter new password"
                            />
                        </div>
                        <div className="flex space-x-2">
                            <button
                                type="button"
                                onClick={handleConfirmPasswordReset}
                                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                Reset Password
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setAwaitingResetCode(false);
                                    setIsResettingPassword(false);
                                    setResetCode('');
                                    setNewPassword('');
                                }}
                                className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Sign Up Button - only show if not in password reset mode */}
                {!isResettingPassword && !awaitingResetCode && (
                    <div>
                        <button
                            type="button"
                            onClick={handleSignUp}
                            className="signup group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Sign Up
                        </button>
                    </div>
                )}
                {awaitingVerification && !isResettingPassword && !awaitingResetCode && (
                    <div className="mt-4 space-y-2">
                        <label htmlFor="verification" className="block text-sm font-medium text-gray-700">
                            Enter Verification Code
                        </label>
                        <input
                            id="verification"
                            name="verification"
                            type="text"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />

                        {/* Verify Button */}
                        <div>
                            {/* Verify Account Button */}
                            <button
                                type="button"
                                onClick={handleConfirmCode}
                                className="verify group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 mt-2"
                            >
                                Verify Account
                            </button>
                            <br/>

                            {/* Resend Code Button */}
                            <button
                                type="button"
                                onClick={handleResendCode}
                                className="resendCode group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 mt-2"
                            >
                                Resend Code
                            </button>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LoginForm;